import { useState } from 'react';
import { MIN_PASSWORD_LENGTH } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import { Shell } from '../components/Shell';

interface Props {
  readonly session: StoredSession;
  readonly onSignOut: () => void;
  readonly onUpdated: (session: StoredSession) => void;
}

export const Account = ({ session, onSignOut, onUpdated }: Props) => {
  const [name, setName] = useState(session.account.name);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const run = async (task: () => Promise<string>) => {
    setError(null);
    setNote(null);
    try {
      setNote(await task());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    }
  };

  return (
    <Shell
      who={session.account.name}
      onSignOut={onSignOut}
      back={{ label: 'All frames', to: '/' }}
    >
      <div className="stack-loose" style={{ marginTop: '2rem' }}>
        {error && <p className="alert">{error}</p>}
        {note && (
          <p className="alert" data-tone="ok">
            {note}
          </p>
        )}

        <div className="stack">
          <div className="section-head">
            <p className="eyebrow">Your name</p>
          </div>
          <p className="note">
            This is what appears under photos you send from now on. Photos already on a frame keep
            the name they were sent under.
          </p>
          <div className="row">
            <label className="field">
              <span>Name</span>
              <input
                className="input"
                value={name}
                maxLength={40}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="button button-quiet"
              disabled={name.trim().length < 2 || name.trim() === session.account.name}
              onClick={() =>
                void run(async () => {
                  const result = await api.rename(session.token, name.trim());
                  onUpdated({ token: session.token, account: result.account });
                  return 'Saved.';
                })
              }
            >
              Save
            </button>
          </div>
        </div>

        <div className="stack">
          <div className="section-head">
            <p className="eyebrow">Password</p>
          </div>
          <p className="note">
            Changing it signs you out everywhere else, which is usually the reason for changing it.
          </p>
          <div className="row">
            <label className="field">
              <span>Current</span>
              <input
                className="input"
                type="password"
                value={current}
                autoComplete="current-password"
                onChange={(event) => setCurrent(event.target.value)}
              />
            </label>
            <label className="field">
              <span>New</span>
              <input
                className="input"
                type="password"
                value={next}
                autoComplete="new-password"
                onChange={(event) => setNext(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="button button-quiet"
              disabled={current.length < 1 || next.length < MIN_PASSWORD_LENGTH}
              onClick={() =>
                void run(async () => {
                  await api.changePassword(session.token, current, next);
                  setCurrent('');
                  setNext('');
                  return 'Password changed. Other devices have been signed out.';
                })
              }
            >
              Change
            </button>
          </div>
        </div>

        <div>
          <div className="section-head">
            <p className="eyebrow">Billing</p>
            <button
              type="button"
              className="button button-quiet button-small"
              onClick={() => go('/billing')}
            >
              Open billing
            </button>
          </div>
          <p className="note">
            Your trial, your subscription and how many frames it covers. People who send photos to
            your frames never pay anything.
          </p>
        </div>

        <div>
          <div className="section-head">
            <p className="eyebrow">Signed in as</p>
          </div>
          <p className="note">{session.account.email}</p>
        </div>
      </div>
    </Shell>
  );
};
